/**
 * Resolves the production-mode API base URL.
 * On web, Vercel Preview hosts (*.vercel.app) use EXPO_PUBLIC_API_URL_PREVIEW when set
 * so the bundle can target staging even if REACT_APP_API_URL_PROD was not applied at build time.
 *
 * Also reads app.config.js `extra.apiUrlPreview` (from the same env at build) via expo-constants
 * when the inline process.env value is missing. expo-constants is require()'d inside the function
 * so Vitest does not load the native-heavy module at test collection time.
 */

export type EnvLike = Record<string, string | undefined>;

export type WindowLocationLike = {
  location: {
    hostname: string;
  };
};

type ExpoExtraApiUrls = {
  apiUrlPreview?: string;
  apiUrlProduction?: string;
};

function readExpoExtraApiUrls(): ExpoExtraApiUrls {
  try {
    const Constants = require('expo-constants').default as {
      expoConfig?: { extra?: ExpoExtraApiUrls };
    };
    return Constants.expoConfig?.extra ?? {};
  } catch {
    return {};
  }
}

export function mergePreviewApiUrlFromExpoExtra(env: EnvLike): EnvLike {
  const out = { ...env };
  if ((out.EXPO_PUBLIC_API_URL_PREVIEW || '').trim()) {
    return out;
  }
  const fromExtra = (readExpoExtraApiUrls().apiUrlPreview || '').trim();
  if (fromExtra) {
    out.EXPO_PUBLIC_API_URL_PREVIEW = fromExtra;
  }
  return out;
}

/** Native release IPAs embed `extra.apiUrlProduction` from app.config at bundle time. */
export function readEmbeddedProductionApiFromExpoExtra(): string {
  try {
    const Constants = require('expo-constants').default as {
      expoConfig?: { extra?: { apiUrlProduction?: string } };
    };
    return String(Constants.expoConfig?.extra?.apiUrlProduction ?? '').trim();
  } catch {
    return '';
  }
}

export function mergeProductionApiUrlFromExpoExtra(env: EnvLike): EnvLike {
  const out = { ...env };
  if (
    (out.EXPO_PUBLIC_API_URL_PROD || '').trim() ||
    (out.REACT_APP_API_URL_PROD || '').trim()
  ) {
    return out;
  }
  const fromExtra = (readExpoExtraApiUrls().apiUrlProduction || '').trim();
  if (fromExtra) {
    out.EXPO_PUBLIC_API_URL_PROD = fromExtra;
  }
  return out;
}

export function resolveProductionApiBaseUrl(
  env: EnvLike,
  windowLike: WindowLocationLike | null | undefined
): string {
  const merged = mergeProductionApiUrlFromExpoExtra(
    mergePreviewApiUrlFromExpoExtra(env),
  );
  const previewBase = (merged.EXPO_PUBLIC_API_URL_PREVIEW || '').trim();
  if (previewBase && windowLike?.location) {
    const host = windowLike.location.hostname.toLowerCase();
    if (host === 'vercel.app' || host.endsWith('.vercel.app')) {
      return previewBase;
    }
  }
  const prod = (
    merged.EXPO_PUBLIC_API_URL_PROD ||
    merged.REACT_APP_API_URL_PROD ||
    'https://api.zporti.com/api/v1'
  ).trim();
  return prod || 'https://api.zporti.com/api/v1';
}
